/**
 * Deliverable schema. PRD-003 DATA-06, TECH-09, AI-01, AI-02.
 *
 * The deliverable `content` payload is JSONB, typed per task_type via a
 * discriminated union. Type-specific renderers (T-058 through T-062) read
 * the typed payload; the GenericMarkdownRenderer (T-057) handles all
 * non-Phase-1 types and the 'other' catch-all.
 */

import { z } from 'zod';
import {
  JSONB_SCHEMA_VERSIONS,
  isoTimestampSchema,
  uuidSchema,
  versionedSchema,
} from './common.js';
import { taskTypeSchema, videoProviderSchema } from './enums.js';
import { sourceSchema } from './source.js';

/**
 * Generic markdown deliverable shape. Used for the 'other' task type and for
 * any non-Phase-1 templated type until its renderer ships.
 */
export const genericMarkdownContentSchema = z.object({
  kind: z.literal('generic_markdown'),
  body_md: z.string().min(1),
});
export type GenericMarkdownContent = z.infer<typeof genericMarkdownContentSchema>;

/**
 * Competitive landscape deliverable. PRD-003 AI-01, T-058.
 */
export const competitiveLandscapeContentSchema = z.object({
  kind: z.literal('competitive_landscape'),
  summary_md: z.string(),
  competitors: z.array(
    z.object({
      name: z.string(),
      what_they_do: z.string(),
      strengths: z.array(z.string()).default([]),
      weaknesses: z.array(z.string()).default([]),
      positioning_note: z.string().optional(),
    }),
  ),
  matrix: z
    .object({
      dimensions: z.array(z.string()),
      rows: z.array(
        z.object({
          competitor: z.string(),
          values: z.array(z.string()),
        }),
      ),
    })
    .optional(),
  positioning_grid: z
    .object({
      x_axis: z.string(),
      y_axis: z.string(),
      points: z.array(
        z.object({
          label: z.string(),
          x: z.number(),
          y: z.number(),
        }),
      ),
    })
    .optional(),
});
export type CompetitiveLandscapeContent = z.infer<typeof competitiveLandscapeContentSchema>;

/**
 * ICP synthesis deliverable. PRD-003 AI-01, T-059.
 */
export const icpSynthesisContentSchema = z.object({
  kind: z.literal('icp_synthesis'),
  primary_audience: z.object({
    label: z.string(),
    summary: z.string(),
    pains: z.array(z.string()).default([]),
    triggers: z.array(z.string()).default([]),
    where_they_are: z.array(z.string()).default([]),
  }),
  secondary_audiences: z
    .array(
      z.object({
        label: z.string(),
        summary: z.string(),
      }),
    )
    .default([]),
  not_serving: z.object({
    summary: z.string(),
    reasons: z.array(z.string()).default([]),
  }),
});
export type IcpSynthesisContent = z.infer<typeof icpSynthesisContentSchema>;

/**
 * Voice encoding deliverable. PRD-003 AI-01, T-060.
 */
export const voiceEncodingContentSchema = z.object({
  kind: z.literal('voice_encoding'),
  voice_attributes: z.array(
    z.object({
      name: z.string(),
      definition: z.string(),
      do_examples: z.array(z.string()).default([]),
      dont_examples: z.array(z.string()).default([]),
    }),
  ),
  banned_phrases: z.array(z.string()).default([]),
  sample_comparisons: z
    .array(
      z.object({
        context: z.string(),
        on_voice: z.string(),
        off_voice: z.string(),
      }),
    )
    .default([]),
});
export type VoiceEncodingContent = z.infer<typeof voiceEncodingContentSchema>;

/**
 * Messaging architecture deliverable. PRD-003 AI-02, T-061.
 */
export const messagingArchitectureContentSchema = z.object({
  kind: z.literal('messaging_architecture'),
  purpose: z.string(),
  positioning: z.string(),
  narrative_arc: z.string(),
  audience_matrix: z.array(
    z.object({
      audience: z.string(),
      headline: z.string(),
      proof_points: z.array(z.string()).default([]),
      call_to_action: z.string().optional(),
    }),
  ),
});
export type MessagingArchitectureContent = z.infer<typeof messagingArchitectureContentSchema>;

/**
 * Pitch deck review deliverable. PRD-003 AI-02, T-062.
 */
export const pitchDeckReviewContentSchema = z.object({
  kind: z.literal('pitch_deck_review'),
  scorecard: z.object({
    clarity: z.number().int().min(1).max(5),
    structure: z.number().int().min(1).max(5),
    differentiation: z.number().int().min(1).max(5),
    voice: z.number().int().min(1).max(5),
    overall_summary: z.string(),
  }),
  slides: z.array(
    z.object({
      slide_index: z.number().int().nonnegative(),
      thumbnail_url: z.string().url().optional(),
      eric_note: z.string(),
      severity: z.enum(['strong', 'okay', 'rework']),
    }),
  ),
});
export type PitchDeckReviewContent = z.infer<typeof pitchDeckReviewContentSchema>;

/**
 * OS handoff deliverable (the gold artifact). PRD-004 scoping-pass §6
 * TECH-OS-01. The body the renderer shows: the README in the client's voice
 * (markdown) plus a one-line mental model (the "say it back" ownership test).
 * The walkthrough video lives on the deliverable row (video_url/video_provider)
 * and the downloadable artifacts live in os_handoff_files + the os-handoffs
 * storage bucket (migration 0030); the OS owner surface composes the always-on
 * video + downloads block around this body (TECH-OS-02: the OS render path
 * always shows the video for the owner, independent of subscriber tier).
 */
export const osHandoffContentSchema = z.object({
  kind: z.literal('os_handoff'),
  readme_md: z.string().min(1),
  mental_model: z.string().min(1),
});
export type OsHandoffContent = z.infer<typeof osHandoffContentSchema>;

/**
 * Discriminated union over deliverable content shapes. Add new entries here
 * when a templated renderer ships; bump JSONB_SCHEMA_VERSIONS.deliverableContent
 * if the existing entries change shape.
 */
export const deliverableContentSchema = z.discriminatedUnion('kind', [
  genericMarkdownContentSchema,
  competitiveLandscapeContentSchema,
  icpSynthesisContentSchema,
  voiceEncodingContentSchema,
  messagingArchitectureContentSchema,
  pitchDeckReviewContentSchema,
  osHandoffContentSchema,
]);
export type DeliverableContent = z.infer<typeof deliverableContentSchema>;

/**
 * Versioned wrapper. Matches `deliverables.content_schema_version`.
 */
export const versionedDeliverableContentSchema = versionedSchema(
  deliverableContentSchema,
  JSONB_SCHEMA_VERSIONS.deliverableContent,
);
export type VersionedDeliverableContent = z.infer<typeof versionedDeliverableContentSchema>;

/**
 * Deliverable row. PRD-003 DATA-06 + TECH-15 (video walkthrough for Pro).
 *
 * Sources are stored as structured records (DATA-17) inline on the deliverable
 * via the `sources` array; mirrors the `sources` table records the row owns.
 */
export const deliverableSchema = z.object({
  id: uuidSchema,
  task_id: uuidSchema,
  content: deliverableContentSchema,
  content_schema_version: z
    .number()
    .int()
    .min(1)
    .default(JSONB_SCHEMA_VERSIONS.deliverableContent),
  format: z.string().default('structured-v1'),
  video_url: z.string().url().nullable(),
  video_provider: videoProviderSchema.nullable(),
  sources: z.array(sourceSchema).default([]),
  published_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
  deleted_at: isoTimestampSchema.nullable(),
});
export type Deliverable = z.infer<typeof deliverableSchema>;

/**
 * Helper: derive the expected deliverable content `kind` for a given task type.
 * Tasks of `other` and non-Phase-1 types should use `generic_markdown`.
 */
export const taskTypeToContentKind = (
  type: z.infer<typeof taskTypeSchema>,
): DeliverableContent['kind'] => {
  switch (type) {
    case 'competitive_landscape':
      return 'competitive_landscape';
    case 'icp_synthesis':
      return 'icp_synthesis';
    case 'voice_encoding':
      return 'voice_encoding';
    case 'messaging_architecture':
      return 'messaging_architecture';
    case 'pitch_deck_review':
      return 'pitch_deck_review';
    default:
      return 'generic_markdown';
  }
};
